import { useEffect, useRef, useCallback } from 'react';
import type { IChartApi, ISeriesApi } from 'lightweight-charts';
import type { MethodologyOverlay } from '@trading/shared';
import { drawMethodologyOverlay, legacyToSMCOverlay, type OverlayDrawContext } from './overlays';

/**
 * Reads a CSS custom property from the document root at call time.
 * Safe to call in canvas draw loops — resolves the current computed value so
 * colors always reflect the active theme.
 *
 * @param variable - CSS custom property name (e.g. `'--loss'`)
 * @param fallback  - Hex fallback used in SSR or when the variable is unset
 */
function getCssColor(variable: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  return value || fallback;
}

interface TradeLevel {
  price: number;
  type: 'entry' | 'stopLoss' | 't1' | 't2' | 't3';
  label?: string;
}

interface AnalysisZone {
  type: 'bullish' | 'bearish';
  top: number;
  bottom: number;
  strength: number;
  status: 'fresh' | 'tested' | 'mitigated';
  time?: number; // Unix timestamp of the candle where this zone was formed
}

interface FairValueGap {
  type: 'bullish' | 'bearish';
  top: number;
  bottom: number;
  filledPercent: number;
  time?: number; // Unix timestamp of the candle where this FVG was created
}

interface LiquidityPool {
  type: 'bsl' | 'ssl';
  price: number;
  strength: number;
  time?: number; // Unix timestamp of the candle where this liquidity was identified
}

interface StructurePoint {
  type: 'bullish' | 'bearish';
  price: number;
  time: number;
}

interface KeyLevels {
  recentHigh: number;
  recentLow: number;
  swingHigh: number;
  swingLow: number;
}

interface SMCData {
  orderBlocks?: AnalysisZone[];
  fvg?: FairValueGap[];
  liquidity?: LiquidityPool[];
  lastBos?: StructurePoint | null;
  lastChoch?: StructurePoint | null;
  keyLevels?: KeyLevels;
}

interface ChartNeonOverlayProps {
  chart: IChartApi | null;
  series: ISeriesApi<'Candlestick'> | null;
  levels: TradeLevel[];
  direction: 'LONG' | 'SHORT' | null;
  isActive: boolean;
  /** Legacy SMC-only payload — preserved for back-compat. */
  smcData?: SMCData;
  /**
   * v3 methodology-aware overlay. When present, wins over `smcData` and
   * renders via the discriminated-union dispatcher in `./overlays/`.
   */
  methodologyOverlay?: MethodologyOverlay | null;
  showSMC?: boolean;
  analysisTime?: number | Date | null;
}

const BOX_WIDTH = 120;
const BOX_MARGIN_LEFT = 15; // Margin from last candle

export default function ChartNeonOverlay({
  chart,
  series,
  levels,
  direction,
  isActive,
  smcData,
  methodologyOverlay,
  showSMC = true,
  analysisTime,
}: ChartNeonOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !chart || !series || !isActive) {
      return;
    }

    const { width, height } = canvas;

    // Validate canvas dimensions
    if (width <= 0 || height <= 0 || !Number.isFinite(width) || !Number.isFinite(height)) {
      return;
    }

    ctx.clearRect(0, 0, width, height);

    // Get time scale for coordinate conversions
    const timeScale = chart.timeScale();

    // Helper to get X coordinate from a timestamp
    const getXFromTime = (time: number | undefined, fallbackX: number): number => {
      if (!time || !Number.isFinite(time)) return fallbackX;
      try {
        const xCoord = timeScale.timeToCoordinate(time as import('lightweight-charts').Time);
        if (xCoord !== null && Number.isFinite(xCoord) && xCoord > 0) {
          return Math.max(xCoord, 20); // Minimum 20px from left
        }
      } catch {
        // If conversion fails, use fallback
      }
      return fallbackX;
    };

    // Helper to safely get Y coordinate from price
    const getYFromPrice = (price: number | undefined): number | null => {
      if (!price || !Number.isFinite(price)) return null;
      try {
        const y = series.priceToCoordinate(price);
        if (y !== null && Number.isFinite(y) && y >= -1000 && y <= height + 1000) {
          return y;
        }
      } catch {
        // If conversion fails, return null
      }
      return null;
    };

    // Calculate default zone positions based on analysis time (fallback for zones without time)
    let defaultZoneStartX = width * 0.65; // fallback

    if (analysisTime) {
      // Convert analysis time to seconds (chart uses seconds)
      const analysisTimestamp = typeof analysisTime === 'number'
        ? analysisTime
        : Math.floor(new Date(analysisTime).getTime() / 1000);

      // Try to get the X coordinate for the analysis time
      try {
        const xCoord = timeScale.timeToCoordinate(analysisTimestamp as import('lightweight-charts').Time);
        if (xCoord !== null && xCoord > 0) {
          defaultZoneStartX = xCoord;
        }
      } catch {
        // If conversion fails, use fallback
      }
    }

    // Ensure zone doesn't start too far left
    defaultZoneStartX = Math.max(defaultZoneStartX, 50);

    // Calculate position after last candle for box placement
    const seriesData = series.data();
    let lastCandleX = width - BOX_WIDTH - 20; // Fallback

    if (seriesData && seriesData.length > 0) {
      const lastCandle = seriesData[seriesData.length - 1];
      if (lastCandle && lastCandle.time) {
        try {
          const xCoord = timeScale.timeToCoordinate(lastCandle.time);
          if (xCoord !== null && Number.isFinite(xCoord)) {
            lastCandleX = xCoord;
          }
        } catch {
          // Use fallback
        }
      }
    }

    // Zones end where the box starts (after last candle)
    const zoneEndX = lastCandleX + BOX_MARGIN_LEFT - 5;

    // Resolve which methodology overlay to draw. Priority:
    //   1. `methodologyOverlay` prop (v3 method-aware, discriminated union)
    //   2. `smcData` prop (legacy SMC-only, mapped to an SMC overlay)
    // The legacy block below remains as a fallback for callers that pass
    // `smcData` without the new union — it is functionally identical to
    // routing through `drawSMCOverlay`, but kept inline to avoid disturbing
    // call sites during the rollout.
    if (showSMC && methodologyOverlay) {
      const drawCtx: OverlayDrawContext = {
        ctx,
        width,
        height,
        chart,
        series,
        defaultZoneStartX,
        zoneEndX,
        getYFromPrice,
        getXFromTime,
      };
      drawMethodologyOverlay(methodologyOverlay, drawCtx);
    } else if (showSMC && smcData) {
      // Legacy path: keep inline SMC drawing for back-compat with old call
      // sites + persisted analyses missing the v3 overlay payload.
      const legacyOverlay = legacyToSMCOverlay({
        zones: {
          orderBlocks: smcData.orderBlocks,
          fvg: smcData.fvg,
          liquidity: smcData.liquidity,
        },
        structure: {
          lastBos: smcData.lastBos ?? null,
          lastChoch: smcData.lastChoch ?? null,
          keyLevels: smcData.keyLevels,
        },
      });
      if (legacyOverlay) {
        const drawCtx: OverlayDrawContext = {
          ctx,
          width,
          height,
          chart,
          series,
          defaultZoneStartX,
          zoneEndX,
          getYFromPrice,
          getXFromTime,
        };
        drawMethodologyOverlay(legacyOverlay, drawCtx);
      }
    }

    // Now draw trade levels (entry, SL, targets)
    if (levels.length === 0) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    // Extract price levels
    const entryLevel = levels.find(l => l.type === 'entry');
    const stopLevel = levels.find(l => l.type === 'stopLoss');
    const t1Level = levels.find(l => l.type === 't1');
    const t2Level = levels.find(l => l.type === 't2');
    const t3Level = levels.find(l => l.type === 't3');

    // Find the furthest target for profit zone
    const targetLevels = [t1Level, t2Level, t3Level].filter(Boolean) as TradeLevel[];
    const furthestTarget = targetLevels.length > 0
      ? (direction === 'LONG'
          ? targetLevels.reduce((max, t) => t.price > max.price ? t : max, targetLevels[0])
          : targetLevels.reduce((min, t) => t.price < min.price ? t : min, targetLevels[0]))
      : null;

    if (!entryLevel) {
      animationRef.current = requestAnimationFrame(draw);
      return;
    }

    try {
      const entryY = getYFromPrice(entryLevel.price);
      if (entryY === null || !Number.isFinite(entryY)) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      // Position the box at the right edge of the chart (not following the last candle)
      const boxX = width - BOX_WIDTH - 5;

      // Draw risk zone (entry to stop loss) - RED with neon glow
      if (stopLevel) {
        const stopY = getYFromPrice(stopLevel.price);
        if (stopY !== null && Number.isFinite(stopY)) {
          const riskTop = Math.min(entryY, stopY);
          const riskHeight = Math.abs(stopY - entryY);

          if (Number.isFinite(riskTop) && Number.isFinite(riskHeight) && riskHeight > 0) {
            ctx.save();
            // Neon glow effect
            ctx.shadowColor = getCssColor('--loss', '#ef4444');
            ctx.shadowBlur = 15;
            ctx.fillStyle = getCssColor('--loss-bg', 'rgba(239, 68, 68, 0.35)');
            ctx.fillRect(boxX, riskTop, BOX_WIDTH, riskHeight);
            ctx.restore();
          }
        }
      }

      // Draw profit zone (entry to furthest target) - GREEN with neon glow
      if (furthestTarget) {
        const targetY = getYFromPrice(furthestTarget.price);
        if (targetY !== null && Number.isFinite(targetY)) {
          const profitTop = Math.min(entryY, targetY);
          const profitHeight = Math.abs(targetY - entryY);

          if (Number.isFinite(profitTop) && Number.isFinite(profitHeight) && profitHeight > 0) {
            ctx.save();
            // Neon glow effect
            ctx.shadowColor = getCssColor('--profit', '#22c55e');
            ctx.shadowBlur = 15;
            ctx.fillStyle = getCssColor('--profit-bg', 'rgba(34, 197, 94, 0.3)');
            ctx.fillRect(boxX, profitTop, BOX_WIDTH, profitHeight);
            ctx.restore();
          }
        }
      }

      // Helper to draw neon label
      const drawNeonLabel = (
        price: number,
        y: number | null,
        label: string,
        glowColor: string,
        showPrice: boolean = true
      ) => {
        if (y === null || y < 0 || y > height) return;

        ctx.save();
        ctx.font = 'bold 10px Inter, sans-serif';

        const labelText = label;
        const labelWidth = ctx.measureText(labelText).width + 12;
        const labelHeight = 18;
        const labelX = boxX + 4;
        const labelY = y - labelHeight / 2;

        // Neon glow for label
        ctx.shadowColor = glowColor;
        ctx.shadowBlur = 12;

        // Label background with glow
        ctx.fillStyle = glowColor;
        ctx.beginPath();
        ctx.roundRect(labelX, labelY, labelWidth, labelHeight, 4);
        ctx.fill();

        // Label text
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(labelText, labelX + 6, y);

        // Price on right side (if enabled)
        if (showPrice) {
          const priceText = price.toFixed(2);
          const priceWidth = ctx.measureText(priceText).width + 10;
          const priceX = boxX + BOX_WIDTH - priceWidth - 4;

          // Neon glow for price
          ctx.shadowColor = glowColor;
          ctx.shadowBlur = 8;
          ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
          ctx.beginPath();
          ctx.roundRect(priceX, labelY, priceWidth, labelHeight, 4);
          ctx.fill();

          ctx.shadowBlur = 0;
          ctx.fillStyle = glowColor;
          ctx.textAlign = 'right';
          ctx.fillText(priceText, priceX + priceWidth - 5, y);
        }

        ctx.restore();
      };

      // Draw labels for each level
      // Stop Loss - loss color neon (no price, just label like targets)
      if (stopLevel) {
        const stopY = getYFromPrice(stopLevel.price);
        drawNeonLabel(stopLevel.price, stopY, 'STOP', getCssColor('--loss', '#ef4444'), false);
      }

      // Entry - info/accent-orange color neon (no price)
      const entryColor = direction === 'LONG'
        ? getCssColor('--info', '#3b82f6')
        : getCssColor('--accent-orange', '#f97316');
      drawNeonLabel(entryLevel.price, entryY, direction === 'LONG' ? 'LONG' : 'SHORT', entryColor, false);

      // Targets - profit color neon (no price, just labels)
      if (t1Level) {
        const t1Y = getYFromPrice(t1Level.price);
        drawNeonLabel(t1Level.price, t1Y, 'T1', getCssColor('--profit', '#22c55e'), false);
      }
      if (t2Level) {
        const t2Y = getYFromPrice(t2Level.price);
        drawNeonLabel(t2Level.price, t2Y, 'T2', getCssColor('--profit', '#22c55e'), false);
      }
      if (t3Level) {
        const t3Y = getYFromPrice(t3Level.price);
        drawNeonLabel(t3Level.price, t3Y, 'T3', getCssColor('--profit-dark', '#10b981'), false);
      }

    } catch {
      // Coordinate conversion may fail during chart transitions
    }

    // Continue animation loop (for smooth updates when scrolling/zooming)
    animationRef.current = requestAnimationFrame(draw);
  }, [chart, series, levels, direction, isActive, smcData, methodologyOverlay, showSMC, analysisTime]);

  // Set up canvas size and start animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const PRICE_SCALE_WIDTH = 55; // Leave space for the price scale

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        const newWidth = parent.clientWidth - PRICE_SCALE_WIDTH;
        const newHeight = parent.clientHeight;
        // Only resize if dimensions actually changed
        if (canvas.width !== newWidth || canvas.height !== newHeight) {
          canvas.width = newWidth;
          canvas.height = newHeight;
          // Redraw after resize
          if (isActive) {
            requestAnimationFrame(draw);
          }
        }
      }
    };

    resizeCanvas();

    // Use ResizeObserver for container resize detection (better than window.resize)
    const parent = canvas.parentElement;
    let resizeObserver: ResizeObserver | null = null;

    if (parent) {
      resizeObserver = new ResizeObserver(() => {
        resizeCanvas();
      });
      resizeObserver.observe(parent);
    }

    // Also listen to window resize as fallback
    window.addEventListener('resize', resizeCanvas);

    // Start animation loop
    if (isActive) {
      animationRef.current = requestAnimationFrame(draw);
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [draw, isActive]);

  // Redraw on chart changes
  useEffect(() => {
    if (!chart || !isActive) return;

    const handleChange = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      animationRef.current = requestAnimationFrame(draw);
    };

    chart.timeScale().subscribeVisibleLogicalRangeChange(handleChange);

    return () => {
      try {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleChange);
      } catch {
        // chart may have been removed before this cleanup runs
      }
    };
  }, [chart, draw, isActive]);

  if (!isActive) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute top-0 left-0 bottom-0 pointer-events-none z-10"
      style={{ right: '55px' }} // Leave space for price scale
    />
  );
}
